import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Helmet } from 'react-helmet-async';
import ImageSequenceScroller from "../components/ImageSequenceScroller";
import InfiniteScroll from '../components/InfiniteScroll';
import WhatsAppNotificationBanner from "../components/WhatsAppNotificationBanner";
import CountUp from '../components/CountUp';
import AnimatedSocialLinks from '../components/AnimatedSocialLinks';
import SErraNDStepper, { Step } from '../components/Stepper';
import CustomStarRating from '../components/CustomStarRating';
import FuzzyText from '../components/FuzzyText';
import ChromaGrid from "../components/ChromaGrid";
import ShinyText from '../components/ShinyText';
import { captureInstallPrompt, triggerInstallPrompt, initializeFirebaseMessaging } from '../serviceWorkerRegistration';
import './Home.css';
import { initializeFCM, setupForegroundNotifications } from '../utils/fcm';

// carouselImages definition
const carouselImages = [
  { src: "https://www.serrand.org/stockphotos/Man-in-Orange-Jacket-in-Industrial-Setting.jpeg", alt: "Industrial Worker in Orange Jacket" },
  { src: "https://www.serrand.org/stockphotos/Chef-in-Pink-Uniform.jpeg", alt: "Chef in Pink Uniform" },
  { src: "https://www.serrand.org/stockphotos/Construction-Workers-at-Site.jpeg", alt: "Construction Team at Site" },
  { src: "https://www.serrand.org/stockphotos/Smiling-Woman-in-Factory.jpeg", alt: "Smiling Factory Worker" },
  { src: "https://www.serrand.org/stockphotos/Culinary-Teamwork-Scene.jpeg", alt: "Culinary Team Collaboration" },
  { src: "https://www.serrand.org/stockphotos/Confident-Construction-Worker.jpeg", alt: "Confident Construction Worker" },
  { src: "https://www.serrand.org/stockphotos/Focused-Construction-Worker.jpeg", alt: "Focused Construction Worker" },
  { src: "https://www.serrand.org/stockphotos/Man-in-Automotive-Workshop.jpeg", alt: "Automotive Workshop Worker" },
  { src: "https://www.serrand.org/stockphotos/Focused-Worker-in-Blue.jpeg", alt: "Worker in Blue Uniform" },
  { src: "https://www.serrand.org/stockphotos/african-american-male-plumber-fixing-a-leak-in-the-kitchen-sink-at-the-customers-home.webp", alt: "African American Male Plumber Fixing Kitchen Sink Leak" },
  { src: "https://www.serrand.org/stockphotos/african-american-male-plumber-repairs-a-broken-water-pipe-under-the-sink.webp", alt: "African American Male Plumber Repairing Water Pipe" },
  { src: "https://www.serrand.org/stockphotos/african-construction-worker-painting-the-wall.webp", alt: "African Construction Worker Painting Wall" },
  { src: "https://www.serrand.org/stockphotos/air-conditioner-cables-and-technician-man-ac-repair-maintenance-or-working-on-electrical.webp", alt: "Technician Working on Air Conditioner Cables" },
  { src: "https://www.serrand.org/stockphotos/barber-giving-a-haircut-in-his-shop.webp", alt: "Barber Giving Haircut in Shop" },
  { src: "https://www.serrand.org/stockphotos/black-female-plumber-at-work.webp", alt: "Black Female Plumber at Work" },
  { src: "https://www.serrand.org/stockphotos/black-female-plumber-fixing-a-leak-in-the-kitchen-sink-of-a-house.webp", alt: "Black Female Plumber Fixing Kitchen Sink Leak" },
  { src: "https://www.serrand.org/stockphotos/black-female-technician-service-open-air-conditioner-indoor-for-checking-and-repairing.webp", alt: "Black Female Technician Servicing Air Conditioner" },
  { src: "https://www.serrand.org/stockphotos/black-man-at-barber-shop.webp", alt: "Black Man at Barber Shop" },
  { src: "https://www.serrand.org/stockphotos/car-with-optics-in-foam-at-the-car-wash-in-the-garage-car-care-and-car-wash-in-the-detailing.webp", alt: "Car Being Washed in Garage" },
  { src: "https://www.serrand.org/stockphotos/close-up-of-a-customers-head-receiving-a-haircut-from-his-barber-in-africa.webp", alt: "Close-Up of Haircut in African Barber Shop" },
  { src: "https://www.serrand.org/stockphotos/electrical-technician-tests-wiring-polarity-grounding-voltages-and-performs-electrical.webp", alt: "Electrical Technician Testing Wiring" },
  { src: "https://www.serrand.org/stockphotos/friends-doing-make-up-at-a-beauty-salon.webp", alt: "Friends Applying Makeup at Beauty Salon" },
  { src: "https://www.serrand.org/stockphotos/little-boy-getting-his-head-shaved-by-barber.webp", alt: "Little Boy Getting Head Shaved by Barber" },
  { src: "https://www.serrand.org/stockphotos/makeup-artist-applying-makeup-on-models-lips.webp", alt: "Makeup Artist Applying Lip Makeup" },
  { src: "https://www.serrand.org/stockphotos/male-plumber-fixing-a-leak-in-the-kitchen-sink-at-the-customers-home.webp", alt: "Male Plumber Fixing Kitchen Sink Leak" },
  { src: "https://www.serrand.org/stockphotos/man-getting-his-hair-cut-at-barber-shop.webp", alt: "Man Getting Haircut at Barber Shop" },
  { src: "https://www.serrand.org/stockphotos/mature-black-man-washing-car.webp", alt: "Mature Black Man Washing Car" },
  { src: "https://www.serrand.org/stockphotos/modeling-makeup-in-the-studio.webp", alt: "Makeup Modeling in Studio" },
  { src: "https://www.serrand.org/stockphotos/painting-is-visual-poetry.webp", alt: "Artist Painting Wall" },
  { src: "https://www.serrand.org/stockphotos/portrait-man-and-plumber-with-smile-maintenance-and-installation-with-home-repairs-face.webp", alt: "Smiling Plumber Portrait" },
  { src: "https://www.serrand.org/stockphotos/professional-makeup-artist-working-with-fashion-model.webp", alt: "Professional Makeup Artist with Fashion Model" },
  { src: "https://www.serrand.org/stockphotos/shot-of-an-attractive-young-woman-getting-a-massage-at-a-spa.webp", alt: "Young Woman Getting Massage at Spa" },
  { src: "https://www.serrand.org/stockphotos/some-well-needed-me-time.webp", alt: "Relaxing Spa Treatment" },
  { src: "https://www.serrand.org/stockphotos/street-artist-drawing-graffiti-on-the-wall.webp", alt: "Street Artist Drawing Graffiti" },
  { src: "https://www.serrand.org/stockphotos/the-stress-is-just-melting-away.webp", alt: "Relaxing Massage Session" },
  { src: "https://www.serrand.org/stockphotos/what-a-pleasure.webp", alt: "Pleasant Spa Experience" },
];
// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Component to invalidate map size
function MapInvalidate() {
  const map = useMap();
  useEffect(() => {
    if (map) {
      map.invalidateSize();
    }
  }, [map]);
  return null;
}

const API_URL = process.env.REACT_APP_API_URL;

// Fetch CSRF token from endpoint
let cachedCsrfToken = null;
let isFetchingCsrf = false;
const getCsrfToken = async () => {
  if (cachedCsrfToken) {
    return cachedCsrfToken;
  }
  if (isFetchingCsrf) {
    await new Promise(resolve => setTimeout(resolve, 100));
    return cachedCsrfToken || '';
  }
  isFetchingCsrf = true;
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/get-csrf-token/`, {
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
    });
    cachedCsrfToken = response.data.csrfToken;
    return cachedCsrfToken;
  } catch (err) {
    console.error('[Home] CSRF Token fetch error:', err.response?.status, err.response?.data, err.message);
    return '';
  } finally {
    isFetchingCsrf = false;
  }
};

// Fetch providers
const fetchProviders = async ({ queryKey }) => {
  const [, page, searchQuery] = queryKey;
  const trimmedQuery = searchQuery.trim();
  const response = await axios.get(
    `${API_URL}/api/?page=${page}&per_page=8&ordering=-is_promoted,trust_score${trimmedQuery ? `&q=${encodeURIComponent(trimmedQuery)}` : ''}`,
    { withCredentials: true }
  );
  return response.data;
};

// Fetch user profile
const fetchUser = async () => {
  try {
    const csrfToken = await getCsrfToken();
    if (!csrfToken) {
      console.error('[Home] No CSRF token available');
      return null;
    }
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/profile/`, {
      withCredentials: true,
      headers: {
        'X-CSRFToken': csrfToken,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
    });
    return response.data;
  } catch (err) {
    console.error('[Home] Fetch user error:', err.response?.status, err.response?.data, err.message, 'Request Headers:', err.config?.headers, 'Cookies Sent:', document.cookie);
    return null;
  }
};

// Fetch provider images with ETag support
const fetchProviderImages = async ({ queryKey }, queryClient) => {
  const [, providerId] = queryKey;
  const cachedEtag = localStorage.getItem(`etag-provider-${providerId}`) || '';
  try {
    const response = await axios.get(`${API_URL}/api/provider/${providerId}/images/`, {
      withCredentials: true,
      headers: { 'If-None-Match': cachedEtag },
    });

    // Store new ETag if provided
    if (response.headers['etag']) {
      localStorage.setItem(`etag-provider-${providerId}`, response.headers['etag']);
    }

    return response.data.map(image => ({
      src: image.image,
      alt: `Image for provider ${providerId}`,
    }));
  } catch (error) {
    if (error.response?.status === 304) {
      // Use cached data if available
      const cachedData = queryClient.getQueryData(['providerImages', providerId]);
      if (cachedData) return cachedData;
      throw new Error('No cached data available');
    }
    throw error;
  }
};

// Fetch provider thumbnails with ETag support (for homepage cards)
const fetchProviderThumbnails = async ({ queryKey }, queryClient) => {
  const [, providerId] = queryKey;
  const cachedEtag = localStorage.getItem(`etag-thumbnails-${providerId}`) || '';
  try {
    const response = await axios.get(`${API_URL}/api/provider/${providerId}/thumbnails/`, {
      withCredentials: true,
      headers: { 'If-None-Match': cachedEtag },
    });

    // Store new ETag if provided
    if (response.headers['etag']) {
      localStorage.setItem(`etag-thumbnails-${providerId}`, response.headers['etag']);
    }

    return response.data.map(thumb => ({
      src: thumb.thumbnail_url,
      alt: `Thumbnail for provider ${providerId}`,
    }));
  } catch (error) {
    if (error.response?.status === 304) {
      // Use cached thumbnails
      const cachedData = queryClient.getQueryData(['providerThumbnails', providerId]);
      if (cachedData) return cachedData;
      // Fallback to empty array if no cache
      return [];
    }
    console.error('Thumbnail fetch error:', error);
    return [];  // Graceful fallback
  }
};

function Home() {
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [userType, setUserType] = useState('');
  const [formData, setFormData] = useState({});
  const [showTutorial, setShowTutorial] = useState(() => !localStorage.getItem('tutorialCompleted'));
  const [errors, setErrors] = useState({});
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showSwipeCue, setShowSwipeCue] = useState(() => {
    const shouldShow = !localStorage.getItem('hideSwipeCue') && !sessionStorage.getItem('hasSwiped');
    return shouldShow;
  });
  const [pulseComplete, setPulseComplete] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [showPassword1, setShowPassword1] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [showNotificationsPrompt, setShowNotificationsPrompt] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [hasPrompted, setHasPrompted] = useState(false);
  const [providerCount, setProviderCount] = useState(0);
  const [showUserTypeSelection, setShowUserTypeSelection] = useState(true);
  const navigate = useNavigate();
  const [showReferralOptions, setShowReferralOptions] = useState(false);
  const queryClient = useQueryClient();
  const loginFormRef = useRef(null);
  const registerFormRef = useRef(null);
  const [providers, setProviders] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  const getReferralIcon = (value) => {
    const map = {
      friend: 'fas fa-user-friends',
      admin_intro: 'fas fa-handshake',
      facebook: 'fab fa-facebook-f',
      instagram: 'fab fa-instagram',
      whatsapp: 'fab fa-whatsapp',
      tiktok: 'fab fa-tiktok',
      twitter: 'fab fa-x-twitter',
      google: 'fab fa-google',
      ai: 'fas fa-robot',
      poster: 'fas fa-bullhorn',
      radio_tv: 'fas fa-radio',
      other: 'fas fa-question-circle',
    };
    return map[value] || 'fas fa-question-circle';
  };

  const getReferralIconClass = (value) => {
    const brand = ['facebook', 'instagram', 'whatsapp', 'tiktok', 'twitter', 'google'];
    return brand.includes(value) ? 'text-blue-400' : 'text-green-400';
  };

  const getReferralLabel = (value) => {
    const map = {
      friend: 'Friend or Family',
      admin_intro: 'Admin Introduced Me',
      facebook: 'Facebook',
      instagram: 'Instagram',
      whatsapp: 'WhatsApp',
      tiktok: 'TikTok',
      twitter: 'X / Twitter',
      google: 'Google Search',
      ai: 'AI (Grok, ChatGPT, etc.)',
      poster: 'Poster',
      radio_tv: 'Radio / TV',
      other: 'Other',
    };
    return map[value] || 'Tap to select';
  };

  const whyItems = [
    {
      content: (
        <div>
          <h3 className="text-xl font-semibold mb-2">Fast Like a Boda 🏍️</h3>
          <p className="text-gray-400">
            Book a guru in minutes, with real-time updates smoother than a Nairobi sunset. 🌅
          </p>
        </div>
      )
    },
    {
      content: (
        <div>
          <h3 className="text-xl font-semibold mb-2">Mtaani Hustle 💼</h3>
          <p className="text-gray-400">
            We're creating gigs for Kenya's youth, from dog walking in Karen to deliveries in Dandora. 🐶🚚
          </p>
        </div>
      )
    },
    {
      content: (
        <div>
          <h3 className="text-xl font-semibold mb-2">Trust is Our Jina ✅</h3>
          <p className="text-gray-400">
            Vetted pros and trust scores mean you're always in safe hands, no stress. 😌
          </p>
        </div>
      )
    },
    {
      content: (
        <div>
          <h3 className="text-xl font-semibold mb-2">Vibes & Convenience 🍻</h3>
          <p className="text-gray-400">
            From last-minute fixes to planned tasks, we make life chill like a Tusker on a Friday. 🍺
          </p>
        </div>
      )
    }
  ];

  const { data: providersData, isLoading: providersLoading, error: providersError, isFetching } = useQuery({
    queryKey: ['providers', page, searchQuery],
    queryFn: fetchProviders,
    keepPreviousData: true,
    cacheTime: 1000 * 60 * 30,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: fetchUser,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/categories/`, { withCredentials: true });
      return res.data;
    },
    staleTime: 10 * 60 * 1000, // 10 min
  });

  function ProviderCard({ provider, handleOrderClick }) {
    const queryClient = useQueryClient();
    const { data: providerImages = [], isLoading: imagesLoading } = useQuery({
      queryKey: ['providerThumbnails', provider.id],  // Changed key to avoid cache conflict
      queryFn: (queryKey) => fetchProviderThumbnails(queryKey, queryClient),  // Use new fetch function
      staleTime: 60 * 60 * 1000,
      cacheTime: 24 * 60 * 60 * 1000,
      enabled: !!provider.id,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

    const uniqueCategorySlugs = [
      ...new Set(provider.services?.map(s => s.category).filter(Boolean) ?? [])
    ];
    const displayCategory =
      provider.services?.length > 0 && provider.services[0].category
        ? uniqueCategorySlugs.length > 1
          ? (categories.find(c => c.is_general)?.name ?? 'General Services')
          : provider.services[0].category_name ?? 'Not set'
        : 'Not set';

    const minCharge =
      provider.services?.length > 0 && provider.services[0].category
        ? uniqueCategorySlugs.length > 1
          ? Math.min(
              ...provider.services.map(s =>
                s.custom_minimum_charge ??
                (categories.find(c => c.is_general)?.default_minimum_charge ?? 300)
              )
            )
          : provider.services[0].custom_minimum_charge ??
            (categories.find(c => c.slug === provider.services[0].category)
              ?.default_minimum_charge ?? 300)
        : 300;

    const fallbackImage = [{
      src: provider.images || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg',
      alt: provider.business_name || provider.username,
    }];
    const imagesToDisplay = providerImages.length > 0 ? providerImages : fallbackImage;
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 overflow-hidden hover:border-blue-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group">
        <div className="relative h-48 bg-gray-800 overflow-hidden">
          {imagesLoading ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-800">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              <ImageSequenceScroller
                images={imagesToDisplay}
                interval={3000}
                style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center top", zIndex: 0 }}
                lazyLoad={true}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-300"></div>
              {provider.is_promoted && (
                <div className="absolute top-3 left-3 bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-3 py-1 rounded-full text-xs font-bold z-10">
                  PROMOTED
                </div>
              )}
              <div className="absolute top-3 right-3 flex items-center gap-1 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs font-semibold z-10">
                {(() => {
                  const hasRating = provider.rating_count > 0;
                  const rating = hasRating
                    ? provider.average_rating
                    : 4.0 + Math.random() * 1.0; // 4.0 to 5.0

                  return (
                    <>
                      <CustomStarRating
                        count={5}
                        value={rating}
                        size={12}
                        onChange={() => {}}
                        readOnly
                      />
                      <span>
                        {hasRating
                          ? `${rating.toFixed(1)} (${provider.rating_count})`
                          : `${rating.toFixed(1)} (New)`
                        }
                      </span>
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>

        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-xl font-bold text-white mb-1 group-hover:text-blue-400 transition-colors">
              {provider.business_name || provider.username}
            </h3>
            <p className="text-blue-400 text-sm font-medium">
              {provider.service_provided || 'General Services'}
            </p>
            <p className="text-gray-400 text-sm">Location: {provider.location || 'Kenya'}</p>

            <p className="text-purple-400 text-sm font-medium mt-1">
              {displayCategory}
            </p>
            <p className="text-green-400 text-sm font-semibold">
              Starts at: {minCharge} KES
            </p>
          </div>

          {provider.description && (
            <p className="text-gray-300 text-sm mb-4 line-clamp-2">
              {provider.description}
            </p>
          )}

          <div className="flex gap-2">
            <button
              onClick={() => handleOrderClick(provider.id)}
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-all transform hover:scale-105"
            >
              Order
            </button>
          </div>

          <Link
            to={`/provider/${provider.id}`}
            className="block text-center text-blue-400 hover:text-blue-300 font-medium text-sm mt-3 transition-colors"
          >
            View Full Profile
          </Link>
        </div>
      </div>
    );
  }

  // Detect PWA status
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsPWA(isStandalone);
  }, []);

  // Fetch initial provider count
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/provider-count/`, { withCredentials: true })
      .then(response => response.json())
      .then(data => setProviderCount(data.total_count))
      .catch(error => console.error('Error fetching provider count:', error));
  }, []);

  // WebSocket for real-time provider count updates
  useEffect(() => {
    try {
      // Force 'ws:' for dev environment, use 'wss:' for production HTTPS
      const wsProtocol = process.env.NODE_ENV === 'development' ? 'ws:' : window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/ws/provider-count-updates/`;
      console.log('Attempting WebSocket connection to:', wsUrl); // Debug log
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => console.log('WebSocket connected for provider count');
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setProviderCount(data.total_count);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      ws.onerror = (error) => console.error('WebSocket error:', error);
      ws.onclose = () => console.log('WebSocket disconnected for provider count');
      return () => {
        ws.close();
      };
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
    }
  }, []);

  // Handle install prompt
  useEffect(() => {
    captureInstallPrompt((prompt) => {
      setCanInstall(true);
    });
  }, []);

  // Handle FCM token retrieval and foreground notifications
  useEffect(() => {
    if (
      user &&
      !hasPrompted &&
      'Notification' in window &&
      (isPWA || !/iPhone|iPad|iPod/.test(navigator.userAgent))
    ) {
      initializeFCM(user, setShowNotificationsPrompt, setHasPrompted);
    }

    const unsubscribe = setupForegroundNotifications(navigate);
    return () => unsubscribe();
  }, [user, hasPrompted, isPWA, navigate]);

  useEffect(() => {
    if (providersError) console.error('Providers fetch error:', providersError);
  }, [providersError]);

  useEffect(() => {
    if (providersData && (providersData.page === page || providersData.page === undefined)) {
      console.log('Updating providers:', { providersLength: providersData.providers?.length || 0, totalPages: providersData.pages || 1, page });
      setProviders(providersData.providers || []);
      setTotalPages(providersData.pages || 1);
    } else if (providersData) {
      console.log('Providers data mismatch:', { providersDataPage: providersData.page, currentPage: page });
    }
  }, [providersData, page]);

  const filteredProviders = providers;

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    navigate(`/?q=${searchQuery}`);
    handleScrollToTopGurus(e);
  };

  const handleLogout = async () => {
    try {
      setIsAuthLoading(true);
      await axios.post(`${API_URL}/api/logout/`, {}, { withCredentials: true });
      queryClient.removeQueries(['user']);
      queryClient.setQueryData(['user'], null);
      navigate('/');
      setIsMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    localStorage.setItem('tutorialCompleted', 'true');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: undefined });
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e.target.checked);
    setErrors({ ...errors, terms: undefined });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!userType) {
      setErrors({ all: ['Please select a user type (Regular User or Service Provider).'] });
      setShowUserTypeSelection(true);
      return;
    }
    if (userType === 'provider') {
      if (!formData.service_provided) {
        setErrors({ service_provided: ['Service offered is required for providers.'] });
        return;
      }
      if (!formData.business_name) {
        setErrors({ business_name: ['Business name is required for providers.'] });
        return;
      }
    }
    if (!termsAccepted) {
      setErrors({ terms: ['You must agree to the Terms of Service and Privacy Policy.'] });
      return;
    }
    setIsAuthLoading(true);
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        data.append(key, value);
      }
    });
    data.append('user_type', userType);
    try {
      await axios.post(`${API_URL}/api/register/`, data, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowRegister(false);
      setShowLogin(true);
      setTermsAccepted(false);
      setFormData({});
      setErrors({});
      setShowUserTypeSelection(true);
      setIsMenuOpen(false);
    } catch (error) {
      const errorData = error.response?.data?.errors || {
        all: ['Registration failed—please check your inputs or try again later.'],
      };
      setErrors(errorData);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsAuthLoading(true);
    try {
      const csrfToken = await getCsrfToken();
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/login/`, formData, {
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        }
      });

      queryClient.invalidateQueries(['user']);
      setShowLogin(false);
      setFormData({});
      setErrors({});
      setIsMenuOpen(false);
    } catch (error) {
      console.error('[Home] Login Error:', error.response?.status);
      setErrors(error.response?.data?.errors || { all: ['Login failed'] });
    } finally {
      setIsAuthLoading(false);
    }
  };


  const handleOrderClick = (providerId) => {
    if (!user) {
      setShowLogin(true);
      setIsMenuOpen(false);
      return;
    }
    navigate(`/provider/${providerId}?order=true`);
  };

  const handleScrollToTopGurus = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const topGurusSection = document.getElementById('top-gurus');
    if (topGurusSection) {
      topGurusSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.error('Element #top-gurus not found in DOM');
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const closeModal = () => {
    setShowLogin(false);
    setShowRegister(false);
    setFormData({});
    setErrors({});
    setTermsAccepted(false);
    setShowUserTypeSelection(true);
  };

  useEffect(() => {
    const hoverElements = document.querySelectorAll('.hover-text');
    hoverElements.forEach((el) => {
      const originalText = el.textContent;
      el.addEventListener('mouseover', () => (el.textContent = el.dataset.secret));
      el.addEventListener('mouseout', () => (el.textContent = originalText));
    });
    return () => hoverElements.forEach((el) => {
      el.removeEventListener('mouseover', () => {});
      el.removeEventListener('mouseout', () => {});
    });
  }, []);

  const pageTitle = "SErraND | Plug Wa Kazi - Connect with Trusted Service Providers in Kenya";
  const pageDescription = "Find reliable local service providers in Kenya with SErraND, your Plug Wa Kazi. From plumbing to deliveries, connect with vetted professionals for all your needs.";
  const pageKeywords = "SErraND, Plug Wa Kazi, service providers, Kenya, local services, hire professionals, trusted gurus, Nairobi, errands, hustlers, mtu wa kazi, kazi, mboka, work, service, services near me, workers";
  const canonicalUrl = "https://www.serrand.org";
  const ogImage = "https://www.serrand.org/static/images/og-image.jpg";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SErraND",
    "url": "https://www.serrand.org",
    "description": pageDescription,
    "publisher": {
      "@type": "Organization",
      "name": "SErraND",
      "url": "https://www.serrand.org",
      "logo": "https://www.serrand.org/images/logo512.png"
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://www.serrand.org/?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-x-hidden">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={pageKeywords} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Marvin Useki" />
        <meta name="viewport" content="width=device-width, initial-scale=0.9, maximum-scale=1.0, user-scalable=yes" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="icon" href="/favicon.ico" type="image/x-icon" />
        <link rel="apple-touch-icon" href="/logo192.png" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content="https://www.serrand.org/stockphotos/Chef-in-Pink-Uniform.jpeg" />
        <meta property="og:image" content="https://www.serrand.org/stockphotos/Confident-Construction-Worker.jpeg" />
        <meta property="og:image" content="https://www.serrand.org/stockphotos/Focused-Worker-in-Blue.jpeg" />
        <meta property="og:site_name" content="SErraND" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://www.serrand.org/stockphotos/Chef-in-Pink-Uniform.jpeg" />
        <meta name="twitter:image:alt" content="Chef in Pink Uniform" />
        <meta name="twitter:image" content="https://www.serrand.org/stockphotos/Confident-Construction-Worker.jpeg" />
        <meta name="twitter:image:alt" content="Confident Construction Worker" />
        <meta name="twitter:image" content="https://www.serrand.org/stockphotos/Focused-Worker-in-Blue.jpeg" />
        <meta name="twitter:image:alt" content="Focused Worker in Blue" />
        <meta name="twitter:site" content="@SErraND" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      {/* Animated Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-700 backdrop-blur-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-white bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                  Welcome Back! 👋
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      name="username_or_email"
                      onChange={handleInputChange}
                      placeholder="Username or Email"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      required
                    />
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                  </div>

                  <div className="relative">
                    <input
                      name="password"
                      onChange={handleInputChange}
                      placeholder="Password"
                      type={showPassword ? 'text' : 'password'}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                  </div>
                </div>

                {errors.all && Array.isArray(errors.all) && (
                  <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                    <p className="text-red-400 text-sm">{errors.all[0]}</p>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <Link
                    to="/forgot-password"
                    className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
                  >
                    Forgot Password?
                  </Link>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={isAuthLoading}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isAuthLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Logging in...
                      </div>
                    ) : (
                      'Login'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-all"
                  >
                    Cancel
                  </button>
                </div>

                <div className="text-center pt-4 border-t border-gray-600">
                  <p className="text-gray-400 text-sm">
                    Don't have an account?{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setShowLogin(false);
                        setShowRegister(true);
                      }}
                      className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                    >
                      Register here
                    </button>
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Register Modal */}
      {showRegister && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-gray-700 backdrop-blur-lg">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-white bg-gradient-to-r from-green-400 to-blue-600 bg-clip-text text-transparent">
                  Join SErraND Squad! 🚀
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                  aria-label="Close modal"
                >
                  ×
                </button>
              </div>

              {showUserTypeSelection ? (
                <div className="space-y-6">
                  <p className="text-gray-300 text-lg text-center">
                    Are you looking for services or offering them?
                  </p>
                  <div className="grid grid-cols-1 gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setUserType('user');
                        setShowUserTypeSelection(false);
                      }}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                    >
                      Join as Customer
                      <p className="text-sm text-gray-200 mt-1">Looking for a Mtu wa Kazi to help with errands or tasks</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUserType('provider');
                        setShowUserTypeSelection(false);
                      }}
                      className="bg-gradient-to-r from-green-500 to-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                    >
                      Join as Service Provider
                      <p className="text-sm text-gray-200 mt-1">Offering services like plumbing, cleaning, or deliveries</p>
                    </button>
                  </div>
                  <div className="text-center pt-4">
                    <p className="text-gray-400 text-sm">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setShowRegister(false);
                          setShowLogin(true);
                        }}
                        className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                      >
                        Login here
                      </button>
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="text-gray-300 text-sm mb-4">
                    Registering as: <span className="font-semibold">{userType === 'user' ? 'Regular User' : 'Service Provider'}</span>
                    {' '}
                    <button
                      type="button"
                      onClick={() => setShowUserTypeSelection(true)}
                      className="text-blue-400 hover:text-blue-300 underline"
                    >
                      Change
                    </button>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div className="relative">
                      <input
                        name="username"
                        onChange={handleInputChange}
                        placeholder="Username"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        required
                      />
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                    </div>

                    <div className="relative">
                      <input
                        name="email"
                        onChange={handleInputChange}
                        placeholder="Email"
                        type="email"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        required
                      />
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                    </div>
                  </div>

                  <div className="relative">
                    <input
                      name="location"
                      onChange={handleInputChange}
                      placeholder="Location (e.g., Westlands)"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                      required
                    />
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <div className="relative">
                      <input
                        name="password1"
                        onChange={handleInputChange}
                        placeholder="Password"
                        type={showPassword1 ? 'text' : 'password'}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword1(!showPassword1)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        aria-label={showPassword1 ? 'Hide password' : 'Show password'}
                      >
                        {showPassword1 ? '🙈' : '👁️'}
                      </button>
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                    </div>

                    <div className="relative">
                      <input
                        name="password2"
                        onChange={handleInputChange}
                        placeholder="Confirm Password"
                        type={showPassword2 ? 'text' : 'password'}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword2(!showPassword2)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                        aria-label={showPassword2 ? 'Hide password' : 'Show password'}
                      >
                        {showPassword2 ? '🙈' : '👁️'}
                      </button>
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                    </div>
                  </div>

                  {/* WhatsApp Number - with +254 auto-filled */}
                  <div className="relative">
                    <div className="flex items-center gap-0 border border-gray-600 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-green-500 focus-within:border-transparent">
                      <div className="bg-gray-800 px-4 py-3 text-gray-400 font-medium border-r border-gray-600 flex items-center gap-2">
                        <i className="fab fa-whatsapp text-green-500"></i>
                        <span>+254</span>
                      </div>
                      <input
                        name="whatsapp_notification_number"
                        type="tel"
                        placeholder="712345678"
                        value={(formData.whatsapp_notification_number || '').replace('+254', '')}
                        onChange={(e) => {
                          let num = e.target.value.replace(/\D/g, '').slice(0, 9); // only digits, max 9
                          handleInputChange({
                            target: {
                              name: 'whatsapp_notification_number',
                              value: num ? '+254' + num : ''
                            }
                          });
                        }}
                        className="flex-1 px-4 py-3 bg-gray-700 text-white placeholder-gray-400 focus:outline-none"
                        required={false}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-2 ml-1">Order updates will be sent here (Recommended)</p>
                  </div>

                  {/* How did you hear about us? - Collapsible */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-300">
                      How did you hear about SErraND?
                    </label>

                    {/* Selected Option Button */}
                    <button
                      type="button"
                      onClick={() => setShowReferralOptions(!showReferralOptions)}
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-left flex items-center justify-between hover:bg-gray-600 transition-all"
                    >
                      <span className="flex items-center gap-3">
                        {formData.how_did_you_hear ? (
                          <>
                            <i className={`fa-fw ${formData.how_did_you_hear ? 
                              (['facebook','instagram','whatsapp','tiktok','twitter','google'].includes(formData.how_did_you_hear) ? 'fab' : 'fas')
                              : 'fas'} 
                              fa-${getReferralIcon(formData.how_did_you_hear).replace(/^(fas|fab) fa-/, '')} 
                              ${formData.how_did_you_hear ? 'text-blue-400' : 'text-gray-500'}`}>
                            </i>
                            <span>{getReferralLabel(formData.how_did_you_hear)}</span>
                            {formData.how_did_you_hear === 'other' && formData.how_did_you_hear_other && (
                              <span className="text-blue-400 text-sm">({formData.how_did_you_hear_other})</span>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-500">Tap to select...</span>
                        )}
                      </span>
                      <i className={`fas fa-chevron-down transition-transform ${showReferralOptions ? 'rotate-180' : ''}`}></i>
                    </button>

                    {/* Dropdown Options */}
                    {showReferralOptions && (
                      <div className="grid grid-cols-1 gap-2 border border-gray-600 rounded-lg p-3 bg-gray-800 max-h-64 overflow-y-auto">
                        {[
                          { value: 'friend', icon: 'user-friends', label: 'Friend or Family', type: 'fas' },
                          { value: 'admin_intro', icon: 'handshake', label: 'Admin Introduced Me', type: 'fas' },
                          { value: 'facebook', icon: 'facebook-f', label: 'Facebook', type: 'fab' },
                          { value: 'instagram', icon: 'instagram', label: 'Instagram', type: 'fab' },
                          { value: 'whatsapp', icon: 'whatsapp', label: 'WhatsApp', type: 'fab' },
                          { value: 'tiktok', icon: 'tiktok', label: 'TikTok', type: 'fab' },
                          { value: 'twitter', icon: 'x-twitter', label: 'X / Twitter', type: 'fab' },
                          { value: 'google', icon: 'google', label: 'Google Search', type: 'fab' },
                          { value: 'ai', icon: 'robot', label: 'AI (Grok, ChatGPT, etc.)', type: 'fas' },
                          { value: 'poster', icon: 'bullhorn', label: 'Poster', type: 'fas' },
                          { value: 'radio_tv', icon: 'radio', label: 'Radio / TV', type: 'fas' },
                          { value: 'other', icon: 'circle-question', label: 'Other', type: 'fas' },
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                how_did_you_hear: opt.value,
                                how_did_you_hear_other: opt.value === 'other' ? prev.how_did_you_hear_other : ''
                              }));
                              setShowReferralOptions(false);
                            }}
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-700 transition-all text-left w-full"
                          >
                            <i className={`fa-fw text-lg ${opt.type} fa-${opt.icon} text-gray-400`}></i>
                            <span className="text-sm">{opt.label}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Other input - only shows when "Other" is selected */}
                    {formData.how_did_you_hear === 'other' && (
                      <input
                        name="how_did_you_hear_other"
                        type="text"
                        placeholder="e.g. YouTube, Church, School..."
                        value={formData.how_did_you_hear_other || ''}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                      />
                    )}
                  </div>

                  {userType === 'provider' && (
                    <div className="space-y-4 p-4 bg-gray-800 bg-opacity-50 rounded-lg border border-gray-600">
                      <h3 className="text-lg font-semibold text-white mb-2">Provider Details</h3>

                      <div className="relative">
                        <input
                          name="business_name"
                          onChange={handleInputChange}
                          placeholder="Business Name"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          required
                        />
                        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                      </div>

                      <div className="relative">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Service Offered (e.g., Plumbing, Make Up Artist, Exhauster Services)
                        </label>
                        <p className="text-sm text-gray-400 mb-2">
                          Please enter the specific service you provide. Be clear and precise (e.g., "House Painting" instead of "Painting").
                          SErraND will categorize your service appropriately.
                        </p>
                        <input
                          name="service_provided"
                          onChange={handleInputChange}
                          placeholder="Enter your service (e.g., Plumbing)"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                          required
                        />
                        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                        {errors.service_provided && Array.isArray(errors.service_provided) && (
                          <p className="text-red-400 text-sm mt-2">{errors.service_provided[0]}</p>
                        )}
                      </div>

                      <div className="relative">
                        <textarea
                          name="description"
                          onChange={handleInputChange}
                          placeholder="Description - DO NOT input your personal contact information like phone number"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all h-24 resize-none"
                        />
                        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                      </div>

                      <div className="relative">
                        <input
                          name="business_hours"
                          onChange={handleInputChange}
                          placeholder="Work Hours (e.g., 24/7, Mon-Sat 8am-6pm)"
                          className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        />
                        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start space-x-3 p-4 bg-gray-800 bg-opacity-50 rounded-lg border border-gray-600">
                    <input
                      type="checkbox"
                      id="terms"
                      checked={termsAccepted}
                      onChange={handleTermsChange}
                      className="mt-1 w-4 h-4 text-green-600 bg-gray-700 border-gray-600 rounded focus:ring-green-500 focus:ring-2"
                      required
                    />
                    <label htmlFor="terms" className="text-sm text-gray-300 leading-relaxed">
                      I agree to the{' '}
                      <Link to="/terms" className="text-green-400 hover:text-green-300 transition-colors underline" target="_blank">
                        Terms of Service
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy" className="text-green-400 hover:text-green-300 transition-colors underline" target="_blank">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>

                  {errors.terms && (
                    <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                      <p className="text-red-400 text-sm">{errors.terms}</p>
                    </div>
                  )}

                  {errors.all && Array.isArray(errors.all) && (
                    <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                      <p className="text-red-400 text-sm">{errors.all[0]}</p>
                    </div>
                  )}

                  {Object.entries(errors).map(([key, value]) => {
                    if (key === 'all' || key === 'service_provided' || key === 'terms' || !Array.isArray(value)) return null;
                    return (
                      <div key={key} className="bg-red-500 bg-opacity-20 border border-red-500 rounded-lg p-3">
                        <p className="text-red-400 text-sm">{value[0]}</p>
                      </div>
                    );
                  })}

                  <div className="flex gap-3 pt-4">
                    <button
                      type="submit"
                      disabled={isAuthLoading}
                      className="flex-1 bg-gradient-to-r from-green-500 to-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-green-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isAuthLoading ? (
                        <div className="flex items-center justify-center">
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Registering...
                        </div>
                      ) : (
                        'Register'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-800 transition-all"
                    >
                      Cancel
                    </button>
                  </div>

                  <div className="text-center pt-4 border-t border-gray-600">
                    <p className="text-gray-400 text-sm">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setShowRegister(false);
                          setShowLogin(true);
                        }}
                        className="text-blue-400 hover:text-blue-300 transition-colors font-medium"
                      >
                        Login here
                      </button>
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notifications Prompt - Enhanced */}
      {user?.username && showNotificationsPrompt && 'Notification' in window && (
        <section className="py-6 px-4 max-w-4xl mx-auto relative z-10">
          <div className="relative bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl border border-gray-600 p-8 shadow-2xl backdrop-blur-lg">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl opacity-5"></div>
            <button
              onClick={() => {
                setShowNotificationsPrompt(false);
                setHasPrompted(true);
                localStorage.setItem('notificationPromptDismissed', 'true');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white text-2xl transition-colors z-10"
              aria-label="Close notification prompt"
            >
              ×
            </button>

            <div className="relative z-10">
              <div className="text-center mb-6">
                <h2 className="text-3xl font-bold text-white mb-4">Enable Notifications 📲 blown-up"></h2>
                <p className="text-lg text-gray-300 mb-4">
                  Stay updated with service requests, acceptances, and admin messages!
                </p>
                {/iPhone|iPad|iPod/.test(navigator.userAgent) && !isPWA && (
                  <span className="block text-sm text-yellow-400 bg-yellow-400 bg-opacity-10 rounded-lg p-3 border border-yellow-400 border-opacity-30">
                    Add SErraND to your home screen to enable notifications on iOS.
                  </span>
                )}
              </div>

              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    Notification.requestPermission()
                      .then((permission) => {
                        setShowNotificationsPrompt(false);
                        setHasPrompted(true);
                        localStorage.setItem('notificationPromptDismissed', 'true');
                        if (permission === 'granted') {
                          initializeFCM(user, setShowNotificationsPrompt, setHasPrompted);
                        }
                      })
                      .catch((err) => {
                        console.error('Notification permission error:', err);
                        setShowNotificationsPrompt(false);
                        setHasPrompted(true);
                        localStorage.setItem('notificationPromptDismissed', 'true');
                      });
                  }}
                  className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={/iPhone|iPad|iPod/.test(navigator.userAgent) && !isPWA}
                >
                  Enable Notifications
                </button>
                <button
                  onClick={() => {
                    setShowNotificationsPrompt(false);
                    setHasPrompted(true);
                    localStorage.setItem('notificationPromptDismissed', 'true');
                  }}
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:scale-105 transition-all"
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Enhanced Navigation */}
      <nav className="relative z-40 bg-gray-900 bg-opacity-80 backdrop-blur-lg border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                <span className="text-2xl font-bold">SE</span>
                <span className="text-2xl font-bold">RRA</span>
                <span className="text-2xl font-bold">ND</span>
              </div>
            </Link>

            <div className="md:hidden flex items-center space-x-2">
              {user?.username ? (
                <span className="text-gray-300 text-sm">
                  Jambo, <span className="font-semibold text-white">{user.username}</span>! 👋
                </span>
              ) : (
                <>
                  <button
                    onClick={() => setShowLogin(true)}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 py-1 rounded-lg text-xs font-medium hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all transform hover:scale-105"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => setShowRegister(true)}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-1 rounded-lg text-xs font-medium hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all transform hover:scale-105 animate-pulse ring-2 ring-green-400 ring-opacity-75 shadow-lg shadow-green-500/50"
                  >
                    Register
                  </button>
                </>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden relative w-8 h-8 flex flex-col justify-center items-center"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-0.5' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 mt-1 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </button>

            {/* Desktop navigation */}
            <div className="hidden md:flex md:items-center md:space-x-6">
              <Link
                to="/"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Home
              </Link>
              <Link
                to="/profile"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Profile
              </Link>
              <Link
                to="/manage-orders"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Manage Orders
              </Link>
              <Link
                to="/contact"
                className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors hover:bg-gray-800"
              >
                Contact
              </Link>


              {userLoading || isAuthLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-gray-400 text-sm">Loading...</span>
                </div>
              ) : user?.username ? (
                <div className="flex items-center space-x-4">
                  <span className="text-gray-300 text-sm">
                    Jambo, <span className="font-semibold text-white">{user.username}</span>! 👋
                  </span>
                  <button
                    onClick={handleLogout}
                    className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowLogin(true)}
                    className="text-gray-300 hover:text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-gray-800"
                  >
                    Login
                  </button>
                  <button
                    onClick={() => setShowRegister(true)}
                    className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all transform hover:scale-105 animate-pulse ring-2 ring-green-400 ring-opacity-75 shadow-lg shadow-green-500/50"
                  >
                    Register
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50"
              onClick={closeMenu}
              aria-hidden="true"
            ></div>
            <div className="absolute top-full left-0 right-0 bg-gray-900 bg-opacity-95 backdrop-blur-lg border-t border-gray-700 shadow-2xl z-50 animate-in slide-in-from-top-2 duration-200">
              <div className="px-4 py-6 space-y-4">
                <Link
                  to="/"
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800"
                  onClick={closeMenu}
                >
                  Home
                </Link>
                <Link
                  to="/profile"
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800"
                  onClick={closeMenu}
                >
                  Profile
                </Link>
                <Link
                  to="/manage-orders"
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800"
                  onClick={closeMenu}
                >
                  Manage Orders
                </Link>
                <Link
                  to="/contact"
                  className="block text-gray-300 hover:text-white px-3 py-2 rounded-md text-base font-medium transition-colors hover:bg-gray-800"
                  onClick={closeMenu}
                >
                  Contact
                </Link>

                {userLoading || isAuthLoading ? (
                  <div className="flex items-center justify-center space-x-2 py-4">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent

 rounded-full animate-spin"></div>
                    <span className="text-gray-400">Loading...</span>
                  </div>
                ) : user?.username ? (
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMenu();
                    }}
                    className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-3 rounded-lg font-medium hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-all"
                  >
                    Logout
                  </button>
                ) : (
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setShowLogin(true);
                        closeMenu();
                      }}
                      className="w-full text-gray-300 hover:text-white px-4 py-3 rounded-lg font-medium transition-colors hover:bg-gray-800 border border-gray-600"
                    >
                      Login
                    </button>
                    <button
                      onClick={() => {
                        setShowRegister(true);
                        closeMenu();
                      }}
                      className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg font-medium hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all transform hover:scale-105 animate-pulse ring-2 ring-green-400 ring-opacity-75 shadow-lg shadow-green-500/50"
                    >
                      Register
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </nav>

      {/* Enhanced Hero Section */}
      <style>
        {`
          @keyframes typing {
            0% { width: 0; }
            15% { width: 8ch; }
            35% { width: 8ch; }
            50% { width: 0; }
            65% { width: 12ch; }
            85% { width: 12ch; }
            100% { width: 0; }
          }

          @keyframes blink {
            0%, 15%, 35%, 50%, 65%, 85%, 100% { border-color: transparent; }
            7.5%, 25%, 57.5%, 75% { border-color: white; }
          }

          @keyframes contentSwitch {
            0%, 50% { content: "SErraND!"; }
            50.1%, 100% { content: "Plug Wa Kazi!"; }
          }

          .typewriter-container {
            display: inline-flex;
            align-items: baseline;
          }

          .typewriter {
            display: inline-block;
            overflow: hidden;
            white-space: nowrap;
            border-right: 2px solid white;
            width: 0;
            animation: typing 8s steps(12) infinite, blink 0.7s step-end infinite;
          }

          .typewriter::before {
            content: "SErraND!\\00A0\\1F1F0\\1F1EA";
            animation: contentSwitch 8s infinite;
          }

          .flag {
            font-size: 0.8em;
            line-height: 1;
            margin-left: 0.2em;
          }

          @keyframes fadeUp {
            0% { opacity: 0; transform: translateY(30px); }
            100% { opacity: 1; transform: translateY(0); }
          }

          .fade-up {
            opacity: 0;
            animation: fadeUp 0.8s ease-out forwards;
          }

          .fade-delay-1 { animation-delay: 0.2s; }
          .fade-delay-2 { animation-delay: 0.4s; }
          .fade-delay-3 { animation-delay: 0.6s; }
          .fade-delay-4 { animation-delay: 0.8s; }
        `}
      </style>
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <ImageSequenceScroller
            images={carouselImages}
            interval={5000}
            style={{ width: "100%", height: "100%", zIndex: 0, pointerEvents: 'none' }}
            lazyLoad={true}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        </div>

        {/* WhatsApp Notification Banner - Top Right */}
        <div className="absolute top-4 right-4 z-20">
          <WhatsAppNotificationBanner userId={user?.id} initialNumber={user?.whatsapp_notification_number} />
        </div>

        <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
          <div className="mb-8 space-y-6">
            <h1 className="text-6xl md:text-8xl font-bold leading-tight fade-up fade-delay-1">
              <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                Jambo
              </span>
              <br />
              <span className="text-white">from</span>
              <br />
              <span className="typewriter-container">
                <span className="typewriter bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent"></span>
                <span className="flag">🇰🇪</span>
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed fade-up fade-delay-2">
              SErraND connects service providers with clients effortlessly, taking only a 10% commission on transactions. For example, if a plumber charges KSh 700, we take just KSh 70, leaving you with the rest. Non-exploitative and reliable—join our platform today, service providers! 🚀
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mt-8">
            <div className="fade-up fade-delay-3">
              <ShinyText
                text="Meet Our Top Gurus"
                speed={3}
                className="text-2xl font-bold cursor-pointer transform hover:scale-105 transition-all bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent hover:from-blue-300 hover:to-purple-500"
                onClick={(e) => handleScrollToTopGurus(e)}
                onTouchEnd={(e) => handleScrollToTopGurus(e)}
              />
            </div>

            {canInstall && (
              <div className="fade-up fade-delay-4">
                <button
                  onClick={triggerInstallPrompt}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-bold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-black transform hover:scale-105 transition-all shadow-lg text-lg"
                >
                  📱 Install App
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="py-12 px-4 max-w-6xl mx-auto relative z-10 text-center">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-8 shadow-2xl backdrop-blur-lg">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl opacity-0 hover:opacity-5 transition-opacity"></div>
          <p className="text-2xl md:text-3xl font-bold text-white">
            Join or hire our over{' '}
            <CountUp
              from={0}
              to={providerCount}
              separator=","
              duration={2}
              className="bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent"
            />{' '}
            providers already on the platform!
          </p>
        </div>
      </section>

      {/* Enhanced Search Section */}
      <section className="py-16 px-4 max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-10">
          <h2 className="text-4xl font-bold text-white mb-4">Find Your Perfect Guru 🔍</h2>
          <p className="text-xl text-gray-300">Search for any service across Kenya</p>
        </div>

        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto">
          <div className="relative flex-1">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Find a service... (e.g., plumber in Kilimani)"
              className="w-full px-6 py-4 bg-gray-800 bg-opacity-80 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all backdrop-blur-lg"
            />
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 opacity-0 hover:opacity-10 transition-opacity pointer-events-none"></div>
          </div>
          <button
            type="submit"
            className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-bold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transform hover:scale-105 transition-all shadow-lg"
          >
            Search 🔍
          </button>
        </form>
      </section>

      {/* Top Providers Section */}
      <section id="top-gurus" className="py-16 px-4 max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-8">
          <h2 className="text-4xl font-bold text-white mb-4">Meet Our Top Gurus 🌟</h2>
          <p className="text-xl text-gray-300 mb-8">Trusted professionals ready to help you</p>

          {/* PAGINATION AT TOP */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center mb-12 space-x-4">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isFetching}
                className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-6 py-3 rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
              >
                ← Previous
              </button>
              <div className="flex items-center space-x-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      disabled={isFetching}
                      className={`w-10 h-10 rounded-lg font-medium transition-all transform hover:scale-105 ${
                        pageNum === page
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isFetching}
                className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-6 py-3 rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
              >
                Next →
              </button>
            </div>
          )}
        </div>

        {providersLoading && page === 1 ? (
          <div className="flex justify-center items-center py-20">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-gray-300 text-lg">Loading amazing gurus...</p>
            </div>
          </div>
        ) : providersError ? (
          <div className="text-center py-20">
            <div className="bg-red-500 bg-opacity-20 border border-red-500 rounded-xl p-8 max-w-md mx-auto">
              <h3 className="text-xl font-bold text-red-400 mb-2">Oops! Something went wrong</h3>
              <p className="text-red-300">Failed to load providers. Please try again later.</p>
            </div>
          </div>
        ) : filteredProviders.length === 0 ? (
          <div className="text-center py-20">
            <div className="bg-gray-800 bg-opacity-50 border border-gray-600 rounded-xl p-8 max-w-md mx-auto">
              <h3 className="text-xl font-bold text-gray-300 mb-2">No providers found</h3>
              <p className="text-gray-400">Try adjusting your search or check back later.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProviders.map((provider) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  handleOrderClick={handleOrderClick}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-12 space-x-4">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1 || isFetching}
                  className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-6 py-3 rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                >
                  ← Previous
                </button>

                <div className="flex items-center space-x-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        disabled={isFetching}
                        className={`w-10 h-10 rounded-lg font-medium transition-all transform hover:scale-105 ${
                          pageNum === page
                            ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                            : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || isFetching}
                  className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-6 py-3 rounded-lg font-medium hover:from-gray-600 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Why Choose SErraND Section */}
      <section className="py-16 px-4 max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">Why Choose SErraND? 🤔</h2>
          <p className="text-xl text-gray-300">We're not just another app - we're your digital mtaani!</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {whyItems.map((item, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 p-8 hover:border-blue-500 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl opacity-0 hover:opacity-5 transition-opacity"></div>
                <div className="relative z-10">
                  {item.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 px-4 max-w-6xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">Find Us in Kenya 🗺️</h2>
          <p className="text-xl text-gray-300">SErraND HQ - Where the magic happens!</p>
        </div>

        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl border border-gray-700 overflow-hidden shadow-2xl">
          <div className="h-96 relative">
            <MapContainer
              center={[-1.2921, 36.8219]}
              zoom={10}
              style={{ height: '100%', width: '100%' }}
              className="rounded-xl"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker position={[-1.2921, 36.8219]}>
                <Popup>
                  <div className="text-center p-2">
                    <h3 className="font-bold text-lg mb-2">SErraND HQ 🏢</h3>
                    <p className="text-sm text-gray-600 mb-2">Nairobi, Kenya</p>
                    <p className="text-xs text-gray-500">Connecting you with trusted service providers!</p>
                  </div>
                </Popup>
              </Marker>
              <MapInvalidate />
            </MapContainer>
          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="bg-gray-900 bg-opacity-90 backdrop-blur-lg border-t border-gray-700 mt-16">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand Section */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4 group">
                <div className="logo-container bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent group-hover:from-blue-400 group-hover:to-purple-500 transition-all">
                  <span className="text-3xl font-bold">SE</span>
                  <span className="text-3xl font-bold">RRA</span>
                  <span className="text-3xl font-bold">ND</span>
                </div>
              </Link>
              <p className="text-gray-300 text-lg mb-4 leading-relaxed">
                Your trusted platform for connecting with local service providers across Kenya.
                From quick errands to major projects, we've got you covered! 🇰🇪
              </p>
              <AnimatedSocialLinks />
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Quick Links</h3>
              <ul className="">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/profile" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Profile
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/manage-orders" className="text-gray-300 hover:text-white transition-colours hover:underline">
                    Manage Orders
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal & Support */}
            <div>
              <h3 className="text-white font-bold text-lg mb-4">Legal & Support</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/terms" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-gray-300 hover:text-white transition-colors hover:underline">
                    About Us
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* App Install Section */}
          {canInstall && (
            <div className="border-t border-gray-700 pt-8 mt-8">
              <div className="text-center">
                <h3 className="text-white font-bold text-lg mb-4">Get the SErraND App! 📱</h3>
                <p className="text-gray-300 mb-4">Install our app for the best experience</p>
                <button
                  onClick={triggerInstallPrompt}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-xl font-bold hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transform hover:scale-105 transition-all shadow-lg"
                >
                  📱 Install Now
                </button>
              </div>
            </div>
          )}

          {/* Copyright */}
          <div className="border-t border-gray-700 pt-8 mt-8 text-center">
            <p className="text-gray-400">
              © 2026 SErraND. All rights reserved. Made with ❤️ in Kenya 🇰🇪
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Connecting communities, one errand at a time.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
